import { until } from '@vueuse/core';
import { computed, ref } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import { createTask } from '../helpers/api/tasks';
import { emailLocalPartContainsPlusSign } from '../helpers/validation/emailLocalPart';
import { useContactFormGroupsMode } from '../composables/useContactFormGroupsMode';
import { trackChatWidgetOpened, trackContactFormOpened, trackChatWidgetClosed, trackContactFormClosed, trackContactFormSuccess, trackContactFormError, trackContactFormSubmitted, trackContactFormAbandoned } from '../helpers/chatWidgetAnalytics';
import { useCmsAiStore } from '../stores/cmsAiStore';
import { useMainStore } from '../stores/mainStore';

type ChatInstance = ReturnType<typeof useChat>;

/** Chat widget bubble/drawer UI state */
export enum BubbleState {
  Closed = 'closed',
  AiChat = 'ai-chat',
  Inbox = 'inbox',
}

const globalChatInstance = ref<ChatInstance | null>(null);
export const setGlobalChatInstance = (chatInstanceParam: ChatInstance) => {
  globalChatInstance.value = chatInstanceParam;
};

export const useAiChatWidget = () => {
  // Use useState for serializable shared state
  const bubbleState = useState<BubbleState>('aiChatWidget.bubbleState', () => BubbleState.Closed);
  const loadDrawer = useState<boolean>('aiChatWidget.loadDrawer', () => false);

  const recaptcha = useReCaptcha();
  const { executeRecaptcha, recaptchaLoaded } = recaptcha || {};

  // Handles internal lazy loading of the drawer, so that the drawer is not loaded until the user hovers over the bubble.
  const handleFirstHover = () => {
    loadDrawer.value = true;
  };

  const { $t } = useNuxtApp();
  const cmsAiStore = useCmsAiStore();
  const mainStore = useMainStore();
  const { useCustomGroups } = useContactFormGroupsMode();
  const displayChat = computed(() => cmsAiStore.cmsAiSettings?.display_ai_chat || false);
  const aiSettings = computed(() => cmsAiStore.getCmsAiSettings);
  const aiChatEnabled = computed(() => aiSettings.value.ai_chat_enabled);
  const tasksEnabled = computed(() => aiSettings.value.tasks_enabled);
  const aiSearchEnabled = computed(() => aiSettings.value.ai_search_enabled);

  const handleStateChange = (state: BubbleState) => {
    bubbleState.value = state;
    if (state !== 'closed') {
      if (state === 'ai-chat') {
        trackChatWidgetOpened();
      } else {
        trackContactFormOpened();
      }
    }
  };

  const isFormEmpty = (formData: any): boolean => {
    if (!formData) return false;

    const hasName = !!formData.name?.trim();
    const hasEmail = !!formData.email?.trim();
    const hasDescription = !!formData.description?.trim();
    const hasTopic = !!formData.topic;
    const hasOrganization = !!formData.organization;
    const hasSubject = !!formData.subject?.trim();

    return !hasName && !hasEmail && !hasDescription && !hasTopic && !hasOrganization && !hasSubject;
  };

  const handleClose = (formData?: any) => {
    if (bubbleState.value === BubbleState.AiChat) {
      trackChatWidgetClosed();
    } else if (bubbleState.value === BubbleState.Inbox) {
      const isEmpty = isFormEmpty(formData);
      if (isEmpty) {
        trackContactFormClosed();
      } else {
        trackContactFormAbandoned(formData);

      }
    }
    bubbleState.value = BubbleState.Closed;
  };

  const isOpen = computed(() => {
    return bubbleState.value !== BubbleState.Closed;
  });

  const handleSubmit = async (formData: any) => {
    try {
      const writableApi = useRuntimeConfig().public.WRITABLE_BASE_API_URL;

      const parentOrgId = mainStore.parentOrgId;
      const secondaryOrgId = mainStore.secondaryOrgId;

      if (!writableApi) {
        throw new Error('API configuration missing');
      }

      if (!parentOrgId) {
        throw new Error('Organization ID is required');
      }

      if (!formData.email) {
        throw new Error('Email is required');
      }

      if (emailLocalPartContainsPlusSign(String(formData.email))) {
        throw new Error($t('chatAndContact.drawer.contactUs.emailAliasNotSupported'));
      }

      if (!formData.name) {
        throw new Error('Name is required');
      }

      const description = formData.description?.trim();

      if (!description) {
        throw new Error('Description is required');
      }

      const topicId = formData.topic ? Number(formData.topic) : undefined;
      const subject = formData.subject?.trim() || 'Contact Form Submission';


      let recaptchaToken = '';
      if (recaptchaLoaded && executeRecaptcha) {
        await recaptchaLoaded();
        recaptchaToken = await executeRecaptcha('ai_chat_ticket');
      }

      const selectedOrganizationId =
        formData.organization != null && formData.organization !== ''
          ? Number(formData.organization)
          : undefined;

      if (useCustomGroups.value && !selectedOrganizationId) {
        throw new Error('Organization is required');
      }

      const legacyOrganizationId = selectedOrganizationId ?? secondaryOrgId;

      const taskData = {
        subject,
        description,
        parent_organization_id: parentOrgId,
        requester: {
          email: formData.email,
          name: formData.name,
        },
        chat_session_id: formData.chatSessionId,
        ...(topicId && { topic_id: topicId }),
        ...(useCustomGroups.value
          ? { task_mgmt_group_id: selectedOrganizationId }
          : { secondary_organization_id: legacyOrganizationId }),
      };


      trackContactFormSubmitted(formData);
      const result = await createTask({
        endpoint: writableApi,
        taskData,
        recaptcha_token: recaptchaToken,
      });

      trackContactFormSuccess(formData);
      return result;
    } catch (error) {
      trackContactFormError(formData, error);
      console.error('Failed to create task:', error);
      throw error;
    }
  };
  const openAiChat = () => {
    if (!loadDrawer.value) {
      loadDrawer.value = true;
    }
    handleStateChange(BubbleState.AiChat);
  };

  const sendMessage = async (message: string, customData: Record<string, unknown> = {}) => {
    if (!globalChatInstance.value) {
      console.error('Chat instance not initialized');
      return false;
    }

    try {
      await until(() => globalChatInstance.value?.isConnected).toBe(true, { timeout: 5000, throwOnTimeout: true });
      await globalChatInstance.value.sendMessage(message, customData);
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  };

  return {
    bubbleState,
    aiChatEnabled,
    tasksEnabled,
    displayChat,
    aiSearchEnabled,
    isOpen,
    loadDrawer,
    handleStateChange,
    handleClose,
    handleFirstHover,
    handleSubmit,
    chat: readonly(globalChatInstance),
    openAiChat,
    sendMessage,
  };
};
